import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Bots, botModes, botStatuses, modelClass, startBot, stopBot,
  startAllBots, stopAllBots, startBots, stopBots, cancelStartAll,
  operationalStatuses, BotMode, BotStatus,
} from '/imports/api/bots';
import { deriveBotDisplay, isStale, isOperationalStatus, type DisplayTone } from '/imports/api/botLifecycle';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { listFleets, resolveFleet } from '/imports/api/fleets';
import { ModelListPage } from '/imports/ui/ModelListPage';
import { Button } from '/imports/ui/Button';
import { BotTerminal } from '/imports/ui/BotTerminal';
import { PageHeading } from '/imports/ui/PageHeading';
import { Icon } from '/imports/ui/Icon';
import { Select } from '/imports/ui/Select';
import { Input } from '/imports/ui/Input';
import { Badge, BadgeTone } from '/imports/ui/Badge';
import { strings } from '/imports/strings';
import { Publications } from '/imports/publications';
import { useUser, useSubscribe, useTracker, useMethod } from '/imports/hooks';
import { Page } from '/imports/ui/Page';

const modeOptions = [{ value: '', label: 'All modes' }, ...botModes.map((m) => ({ value: m, label: m }))];
const modeEditOptions = botModes.map((m) => ({ value: m, label: m }));
const statusOptions = [{ value: '', label: 'All statuses' }, ...botStatuses.map((s) => ({ value: s, label: s }))];
const activeOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

type ActiveFilterValue = 'active' | 'inactive' | 'all';
const activeQuery = (v: ActiveFilterValue) =>
  v === 'active' ? { active: true }
    : v === 'inactive' ? { active: { $ne: true } }
      : { active: { $in: [true, false] } };

const { pathPrefix, pluralName, icon } = modelClass;

// Tailwind shell replacing the old styled(Page) wrapper (proof of concept).
// Arbitrary-variant rules reach the same ModelListPage internals the styled
// block targeted; `!` (important) lets them beat the unlayered
// styled-components rules, like the old !important override.
//   - h-0/min-h-0 on .page-body: the default ModelListPageStyled sets
//     `.page-body { height: 0 }`, which a custom `styled` prop replaces — this
//     restores it so the flex child is height-bounded and the INNER .table
//     scrolls (sticky header + fixed .catalog-tools), not .page-body.
const BotsPageShell = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={[
      props.className ?? '',
      '[&_.active-inactive-all-filter]:hidden!',
      '[&_.page-body]:h-0! [&_.page-body]:min-h-0!',
      '[@media(max-height:1000px)]:[&_.page-body]:overflow-auto!',
    ].join(' ')}
  />
);

// The display matrix's tone vocabulary, so a new DisplayTone fails the
// exhaustiveness check here rather than silently rendering as grey.
const displayTone: Record<DisplayTone, BadgeTone> = {
  ok: 'green',
  busy: 'blue',
  pending: 'amber',
  recovering: 'gold',
  fault: 'red',
  idle: 'neutral',
  stale: 'gold',
};

const StatusCell = ({ id, fallback }: { id: string; fallback?: BotStatus }) => {
  const bot = useTracker(() => Bots.findOne(id), [id]);
  const status = bot?.status ?? fallback;
  const display = deriveBotDisplay(
    bot?.desiredState,
    status,
    isOperationalStatus(status) && isStale(bot?.lastSeenAt, Date.now()),
  );
  if (!status && !bot?.desiredState) return null;
  return <Badge tone={displayTone[display.tone]}>{display.label}</Badge>;
};

const stoppedStatuses: BotStatus[] = ['offline', 'crashed'];

const ActionCell = ({ id, username, fallback }: { id: string; username: string; fallback?: BotStatus }) => {
  const status = useTracker(() => Bots.findOne(id)?.status, [id]) ?? fallback;
  const running = !!status && !stoppedStatuses.includes(status);
  if (running) {
    return (
      <Button
        className="btn secondary"
        onClick={async () => {
          try {
            await stopBot.call({ username });
            toast.success(`Stopped ${username}`);
          }
          catch (error) {
            toast.error(`${error}`);
          }
        }}
      >
        Stop
      </Button>
    );
  }
  return (
    <Button
      className="btn primary"
      onClick={async () => {
        try {
          await startBot.call({ username });
          toast.success(`Started ${username}`);
        }
        catch (error) {
          toast.error(`${error}`);
        }
      }}
    >
      Start
    </Button>
  );
};


// One summary chip: dark mono badge, count tinted per kind (Tailwind utilities
// replacing the old .bots-summary/.stat styled rules one-for-one).
const Stat = ({ label, count, countClass = 'text-[#f0f0f0]' }: { label: string; count: number; countClass?: string }) => (
  <span className="rounded border border-[#222222] bg-[#161616] px-2 py-1 font-mono text-[#d8d8d8]">
    <span className="mr-1.5 text-[#7a7a7a]">{label}</span>
    <span className={`font-semibold ${countClass}`}>{count}</span>
  </span>
);

const BotsSummary = () => {
  const counts = useTracker(() => {
    const all = Bots.find({}).fetch();
    const byMode = (mode: string) => all.filter((b) => b.mode === mode && b.status === 'running').length;
    const desiredUp = all.filter((b) => b.desiredState === 'up');
    return {
      running: all.filter((b) => b.status === 'running').length,
      inTrade: all.filter((b) => b.status === 'inTrade').length,
      buy: byMode('buy'),
      sell: byMode('sell'),
      banker: byMode('banker'),
      census: byMode('census'),
      stopped: all.filter((b) => b.status === 'offline').length,
      // Bulk-start progress, straight from the field: how many of the bots
      // the operator wants up are actually up. No job-state plumbing.
      desiredUp: desiredUp.length,
      desiredUpLive: desiredUp.filter((b) => isOperationalStatus(b.status)).length,
    };
  }, []);
  return (
    // Same centred column as PageHeading and PageBody, so the chips, the
    // terminal and the catalog share one left edge and one width.
    <div className="mx-auto box-border flex w-full max-w-[1600px] flex-wrap gap-2 px-4 pb-3 text-xs sm:px-6">
      <Stat label="Running" count={counts.running} />
      <Stat label="In Trade" count={counts.inTrade} countClass="text-[#7fc8ff]" />
      <Stat label="Buy (running)" count={counts.buy} />
      <Stat label="Sell (running)" count={counts.sell} />
      <Stat label="Banker (running)" count={counts.banker} />
      <Stat label="Census (running)" count={counts.census} />
      <Stat label="Stopped" count={counts.stopped} countClass="text-[#888888]" />
      {counts.desiredUp > 0 && counts.desiredUpLive < counts.desiredUp && (
        <span className="rounded border border-[#222222] bg-[#161616] px-2 py-1 font-mono text-[#d8d8d8]">
          <span className="mr-1.5 text-[#7a7a7a]">Coming up</span>
          <span className="font-semibold text-[#ffd479]">{counts.desiredUpLive}/{counts.desiredUp}</span>
        </span>
      )}
    </div>
  );
};

export const BotsPage = () => {
  // One fetch for the whole list; fleets change only when an operator edits them.
  const [fleetRes] = useMethod(listFleets, { limit: 500 }, []);
  const fleets = (fleetRes?.documents ?? []) as { _id: string; name: string; prefix: string }[];
  useSubscribe(Publications.bots);
  const [mode, setMode] = useState<BotMode | ''>('');
  const [status, setStatus] = useState<BotStatus | ''>('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilterValue>('active');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Fleet-wide actions are confirmed: both are one click away from restarting
  // or silencing every bot the business runs on.
  const [confirming, setConfirming] = useState<null | 'start' | 'stop'>(null);

  // The server paces these (a classified post between starts, a second between
  // stops), so the method returns as soon as the job is queued. Progress shows
  // up in the table, which is already reactive on bot status.
  const runFleetAction = async (action: 'start' | 'stop') => {
    setConfirming(null);
    try {
      const res = action === 'start' ? await startAllBots.call() : await stopAllBots.call();
      const verb = action === 'start' ? 'Starting' : 'Stopping';
      if (!res.count) toast.info(action === 'start' ? 'No stopped bots to start' : 'No running bots to stop');
      else toast.success(`${verb} ${res.count} bot${res.count === 1 ? '' : 's'}…`);
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };

  // Selection start/stop go through the same server jobs as the fleet-wide
  // buttons: pacing, infra-first ordering and desired-state writes live in
  // ONE place, and a half-started selection no longer dies silently with the
  // browser tab. Progress shows in the table and the "Coming up" chip.
  const runBulk = async (action: 'start' | 'stop') => {
    const usernames = selectedIds
      .map((_id) => Bots.findOne(_id)?.username)
      .filter((u): u is string => !!u);
    if (!usernames.length) return;
    try {
      const res = action === 'start'
        ? await startBots.call({ usernames })
        : await stopBots.call({ usernames });
      const verb = action === 'start' ? 'Starting' : 'Stopping';
      if (!res.count) toast.info(action === 'start' ? 'Nothing to start' : 'Nothing to stop');
      else toast.success(`${verb} ${res.count} bot${res.count === 1 ? '' : 's'}…`);
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };

  // A start run is "in flight" exactly while some bot is desired-up but not
  // yet operational -- whatever queued it. Cancel flips those (and only
  // those) back to desired-down; bots already up stay up.
  const pendingStarts = useTracker(
    () => Bots.find({ desiredState: 'up', status: { $nin: operationalStatuses } } as any).count(),
    [],
  );
  const runCancelStart = async () => {
    try {
      const res = await cancelStartAll.call();
      toast.success(`Cancelled ${res.cancelled} pending start${res.cancelled === 1 ? '' : 's'}`);
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };

  return (
    <ModelListPage
      modelClass={modelClass as any}
      permission={(user) => !!user?.isSuperUser}
      styled={BotsPageShell}
      limit={1000}
      defaultSort={{ username: 1 }}
      query={() => ({ ...activeQuery(activeFilter), ...(mode ? { mode } : {}), ...(status ? { status } : {}) })}
      onSelectedIds={setSelectedIds}
      extraFilters={
        <>
          <Select
            className="filter"
            value={mode}
            options={modeOptions}
            onChange={(v) => setMode(v as BotMode | '')}
          />
          <Select
            className="filter"
            value={status}
            options={statusOptions}
            onChange={(v) => setStatus(v as BotStatus | '')}
          />
          <Select
            className="filter"
            value={activeFilter}
            options={activeOptions}
            onChange={(v) => setActiveFilter(v as ActiveFilterValue)}
          />
          {selectedIds.length > 0 && (
            <>
              <Button className="btn primary" onClick={() => runBulk('start')}>Start ({selectedIds.length})</Button>
              <Button className="btn secondary" onClick={() => runBulk('stop')}>Stop ({selectedIds.length})</Button>
            </>
          )}
        </>
      }
      header={
        <>
          <PageHeading
            left={<h1>{icon ? <Icon icon={icon} /> : null}{pluralName}</h1>}
            right={(
              <>
                <Button className="btn secondary" onClick={() => setConfirming('start')}>Start All</Button>
                {pendingStarts > 0 && (
                  <Button className="btn secondary" onClick={() => void runCancelStart()}>
                    Cancel start ({pendingStarts})
                  </Button>
                )}
                <Button className="btn secondary" onClick={() => setConfirming('stop')}>Stop All</Button>
                <Link to={`${pathPrefix}/new`}><Button className="btn primary new-button">{strings.New()}</Button></Link>
              </>
            )}
          />
          {confirming && (
            <ConfirmModal
              title={confirming === 'start' ? 'Start all bots?' : 'Stop all bots?'}
              body={confirming === 'start'
                ? 'Every active bot that is not already running will be started, one at a time, waiting for each to post its classified before moving on.'
                : 'Every running bot will be stopped immediately and at once. Bots mid-trade will be disconnected.'}
              confirmText={confirming === 'start' ? 'Start All' : 'Stop All'}
              onConfirm={() => void runFleetAction(confirming)}
              onClose={() => setConfirming(null)}
            />
          )}
          <BotsSummary />
          <div className="mx-auto box-border w-full max-w-[1600px] px-4 pb-4 sm:px-6">
            <BotTerminal />
          </div>
        </>
      }
      columns={[
        { field: 'username', label: 'Username', render: (bot) => <Link to={`${modelClass.pathPrefix}/${bot._id}`}>{bot.username as string}</Link> },
        { field: 'mode', label: 'Mode', input: { type: Select, props: { options: modeEditOptions } } },
        { field: 'uid', label: 'UID', className: 'mono' },
        {
          // Derived, not stored: which fleet's messages this bot speaks with.
          // Shown here so a prefix that captures the wrong bots is visible at a
          // glance rather than only when a customer sees the wrong wording.
          // '_id', not 'username': DataTable keys cells by column.field, and a
          // second 'username' column produced duplicate React keys (and a sort
          // header that silently sorted the wrong thing).
          field: '_id', label: 'Fleet',
          render: (bot) => {
            const fleet = resolveFleet(bot.username as string, fleets);
            return fleet
              ? <Link className="text-primary hover:underline" to={`/fleets/${fleet._id}`}>{fleet.name}</Link>
              : <span className="text-fg-3">default</span>;
          },
        },
        { field: 'mfakey', label: 'MFA Key', input: { type: Input } },
        { field: 'status', label: 'Status', render: (bot) => <StatusCell id={String(bot._id)} fallback={bot.status as BotStatus | undefined} /> },
        {
          field: '_id',
          label: 'Action',
          render: (bot) => (
            <ActionCell
              id={String(bot._id)}
              username={bot.username as string}
              fallback={bot.status as BotStatus | undefined}
            />
          ),
        },
      ]}
    />
  );
};

// keep unused import live so tree-shaking doesn't drop the hook
void useUser;
